import { defineQuery, useQuery } from '@pinia/colada';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { type InsightSummary, listInsights, type ProductArea } from '@/apis/insights';
import { CHANNEL_SELECTOR_ROUTE_MAP } from '@/components/layout/navigation/constants';
import { QueryKeys } from '@/shared/queries/keys';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useInsightSessionStore } from '@/stores/insight-session';

const DIRECT_PRODUCT_AREAS: readonly ProductArea[] = ['community', 'dashboards'];

const ANALYTICS_ROUTE_HEADS: ReadonlySet<string> = new Set(
  Object.values(CHANNEL_SELECTOR_ROUTE_MAP).map(({ to }) => to.name),
);

const DETECTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const ACTIVE_AFTER_FIRST_VIEW_MS = 24 * 60 * 60 * 1000;

export function isInsightActiveForUser(insight: InsightSummary, now: number = Date.now()): boolean {
  if (now - new Date(insight.detectedAt).getTime() > DETECTION_WINDOW_MS) return false;
  const insightInteraction = insight.userInsightInteraction;
  if (!insightInteraction) return true;
  if (insightInteraction.interactedAt) return false;
  if (!insightInteraction.firstViewedAt) return true;
  return now - new Date(insightInteraction.firstViewedAt).getTime() < ACTIVE_AFTER_FIRST_VIEW_MS;
}

export function productAreaForRoute(
  routeName: string | symbol | null | undefined,
): ProductArea | null {
  if (typeof routeName !== 'string') return null;
  const head = routeName.split('.')[0];
  if (ANALYTICS_ROUTE_HEADS.has(head)) return 'analytics';
  return (DIRECT_PRODUCT_AREAS as readonly string[]).includes(head) ? (head as ProductArea) : null;
}

export function insightDisplayLabel(insight: InsightSummary): string {
  switch (insight.signalType) {
    case 'community_sentiment_spike':
      if (insight.signalSubtype === 'positive_comments_volume') {
        return 'Detected spike in positive sentiment';
      }
      if (insight.signalSubtype === 'negative_comments_volume') {
        return 'Detected spike in negative sentiment';
      }
      return 'Detected sentiment spike';
    case 'content_performance':
      if (insight.signalSubtype) {
        return `Detected significant changes in ${insight.signalSubtype.replace(/_/g, ' ')}`;
      }
      return 'Detected significant content performance changes';
    default:
      return 'Insight Detected';
  }
}

export const useActiveInsight = defineQuery(() => {
  const { currentBrand } = storeToRefs(useIdentityStore());
  const { dismissedIds } = storeToRefs(useInsightSessionStore());
  const flagStore = useFlagStore();
  const route = useRoute();

  const brandIds = computed<number[]>(() =>
    currentBrand.value?.id ? [currentBrand.value.id] : [],
  );

  const flagEnabled = computed(() => Boolean(flagStore.ready && flagStore.flags.visionAiInsights));

  const { data, ...rest } = useQuery<InsightSummary[]>({
    enabled: computed(() => flagEnabled.value && brandIds.value.length > 0),
    key: () => QueryKeys.insights.list(brandIds.value),
    query: async ({ signal }) => {
      const response = await listInsights({ brandIds: brandIds.value }, { signal });
      return response.data.data;
    },
  });

  const currentProductArea = computed<ProductArea | null>(() => productAreaForRoute(route.name));

  const activeInsight = computed<InsightSummary | null>(() => {
    const insights = data.value;
    const area = currentProductArea.value;
    if (!insights?.length || !area) return null;

    const inArea = insights.filter((insight) => insight.productAreas.split(',').includes(area));
    if (inArea.length === 0) return null;

    const newest = inArea.reduce((acc, candidate) =>
      new Date(candidate.detectedAt).getTime() > new Date(acc.detectedAt).getTime()
        ? candidate
        : acc,
    );

    if (dismissedIds.value.has(newest.id)) return null;
    if (!isInsightActiveForUser(newest, Date.now())) return null;
    return newest;
  });

  return {
    activeInsight,
    allInsights: data,
    currentProductArea,
    ...rest,
  };
});
